import { useEffect, useRef, useState, type JSX, type ReactNode } from 'react';
import type { GenerationWarning, RenditionDto } from '../api/library.js';
import { EmptyState } from '../components/EmptyState.js';
import { ImageViewer } from '../components/ImageViewer.js';
import { GenerationFailed } from '../components/GenerationFailed.js';
import { useI18n } from '../i18n/context.js';

/**
 * Лента вариантов рендера и кнопки над ней (01-SPEC.md §5.5).
 *
 * Варианты не удаляются: перегенерация добавляет ещё один, а не затирает
 * прошлый — пользователь должен иметь возможность вернуться к предыдущему.
 * Утверждение возможно только при выбранном варианте; у вещей и локаций
 * утверждения нет вовсе (`onApprove` не передаётся).
 */
export function RenditionPanel({
  rendition,
  styleReady,
  pending,
  warnings,
  deadlock,
  readOnly = false,
  onGenerate,
  onSelect,
  onEdit,
  onApprove,
  approveNote,
}: {
  rendition: RenditionDto | undefined;
  styleReady: boolean;
  pending: boolean;
  warnings: readonly GenerationWarning[];
  readOnly?: boolean;
  /** Разбор «не похож»: если передан — вместо кнопки «ещё раз» показывается он. */
  deadlock?: ReactNode;
  onGenerate(): void;
  onSelect(variantId: string): void;
  onEdit(variantId: string, guidance: string): Promise<void>;
  onApprove?(): void;
  /**
   * Said beside Approve while it would cost something: for a character, approving deletes
   * the photos the drawing came from, and nothing undoes that.
   */
  approveNote?: string | undefined;
}): JSX.Element {
  const { t } = useI18n();
  const labels = t.library.rendition;
  /** Какой вариант открыт во весь экран: по миниатюре сходство не оценить. */
  const [zoomedIndex, setZoomedIndex] = useState<number | null>(null);
  const [editingVariantId, setEditingVariantId] = useState<string | null>(null);
  const [editGuidance, setEditGuidance] = useState('');
  const variants = rendition?.variants ?? [];
  const generating = rendition?.status === 'generating';
  const approved = rendition?.status === 'approved';
  /*
   * After "Draw", the new drawing is brought into view when it arrives: on a phone the
   * parent was left somewhere else on a long card and never saw what was drawn.
   */
  const listRef = useRef<HTMLUListElement | null>(null);
  const awaitingCount = useRef<number | null>(null);
  useEffect(() => {
    if (awaitingCount.current === null || variants.length <= awaitingCount.current) return;
    awaitingCount.current = null;
    listRef.current?.lastElementChild?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [variants.length]);
  const generate = (): void => {
    awaitingCount.current = variants.length;
    onGenerate();
  };

  if (!styleReady) {
    return (
      <p role="status" className="text-sm text-amber-800">
        {labels.noStyle}
      </p>
    );
  }

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <span role="status" className="text-sm text-[var(--kitup-color-muted)]">
          {labels.statuses[rendition?.status ?? 'empty']}
        </span>
        {rendition && rendition.attempts > 0 ? (
          <span className="text-sm text-[var(--kitup-color-muted)]">
            {labels.attempts(rendition.attempts)}
          </span>
        ) : null}
      </div>

      {rendition?.lastError ? <GenerationFailed error={rendition.lastError} /> : null}

      {warnings.map((warning) => (
        <p key={warning} role="status" className="text-sm text-amber-800">
          {labels.warnings[warning]}
        </p>
      ))}

      {readOnly ? null : (
        <>
          {deadlock}
          <div className="flex flex-wrap gap-3">
            {deadlock ? null : (
              <button
                type="button"
                className="kitup-secondary-button"
                disabled={pending || generating}
                onClick={generate}
              >
                {variants.length === 0 ? labels.generate : labels.regenerate}
              </button>
            )}
            {onApprove ? (
              <button
                type="button"
                className={approved ? 'kitup-secondary-button' : 'kitup-primary-button'}
                disabled={pending || approved || !rendition?.selectedVariantId}
                onClick={onApprove}
              >
                {approved ? labels.approved : labels.approve}
              </button>
            ) : null}
          </div>
          {onApprove && approveNote && !approved ? (
            <p className="text-sm text-[var(--kitup-color-muted)]">{approveNote}</p>
          ) : null}
        </>
      )}

      {/* The section around this panel is already titled "Variants". */}
      {variants.length === 0 ? (
        <EmptyState tone="inline" title={labels.empty} />
      ) : (
        <ul ref={listRef} className="flex flex-wrap gap-3">
          {variants.map((variant, index) => {
            const selected = rendition?.selectedVariantId === variant.id;
            return (
              // The one column is `minmax(0, 1fr)`, not `auto`: an auto column grows to the widest
              // row inside it, and the button row (Select + Edit) is wider than the
              // tile, so the picture was stretched past the tile and over its neighbour.
              <li
                key={variant.id}
                className="grid w-48 grid-cols-[minmax(0,1fr)] content-start gap-1"
              >
                <button
                  type="button"
                  className="kitup-zoomable kitup-rendition-preview"
                  data-selected={selected ? 'true' : 'false'}
                  aria-label={labels.zoom(index + 1)}
                  onClick={() => setZoomedIndex(index)}
                >
                  <img
                    src={variant.url}
                    alt={`${labels.variantsTitle} ${index + 1}`}
                    className="w-full bg-[var(--kitup-color-accent)]/5 object-contain"
                  />
                </button>
                <div className="flex flex-wrap items-center justify-between gap-1">
                  {readOnly ? (
                    selected ? (
                      <span className="text-xs text-[var(--kitup-color-muted)]">
                        {labels.selected}
                      </span>
                    ) : null
                  ) : (
                    <button
                      type="button"
                      aria-pressed={selected}
                      // Choosing is the one thing this tile is for; it was the faintest text on it.
                      className={`${selected ? 'kitup-primary-button' : 'kitup-secondary-button'} min-h-8 px-2.5 py-1 text-xs`}
                      disabled={pending}
                      onClick={() => onSelect(variant.id)}
                    >
                      {selected ? `✓ ${labels.selected}` : labels.select}
                    </button>
                  )}
                  {readOnly ? null : (
                    <button
                      type="button"
                      className="kitup-quiet-button min-h-8 px-2 py-1 text-xs"
                      disabled={pending}
                      aria-expanded={editingVariantId === variant.id}
                      onClick={() => {
                        setEditingVariantId((current) =>
                          current === variant.id ? null : variant.id,
                        );
                        setEditGuidance('');
                      }}
                    >
                      {t.common.editImage}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {editingVariantId ? (
        <form
          className="grid max-w-xl gap-2 rounded-xl border border-[var(--kitup-color-border)] p-3"
          onSubmit={(event) => {
            event.preventDefault();
            const guidance = editGuidance.trim();
            if (!guidance) return;
            void onEdit(editingVariantId, guidance).then(() => {
              setEditingVariantId(null);
              setEditGuidance('');
            });
          }}
        >
          {/* Which variant is being changed: the panel opens below the whole row. */}
          {(() => {
            const editing = variants.find((variant) => variant.id === editingVariantId);
            return editing ? (
              <img
                src={editing.url}
                alt=""
                aria-hidden="true"
                className="w-24 rounded-[var(--kitup-radius-sm)] bg-[var(--kitup-color-accent)]/5 object-contain"
              />
            ) : null;
          })()}
          <label className="grid gap-1 text-sm">
            <span className="font-medium">{t.common.imageEditGuidance}</span>
            <textarea
              className="kitup-input min-h-24 resize-y"
              value={editGuidance}
              maxLength={1_000}
              disabled={pending}
              placeholder={t.common.imageEditPlaceholder}
              onChange={(event) => setEditGuidance(event.target.value)}
            />
          </label>
          <p className="text-xs text-[var(--kitup-color-muted)]">{t.common.imageEditHint}</p>
          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              className="kitup-primary-button"
              disabled={pending || !editGuidance.trim()}
            >
              {pending ? t.common.imageEditing : t.common.applyImageEdit}
            </button>
            <button
              type="button"
              className="kitup-quiet-button"
              disabled={pending}
              onClick={() => {
                setEditingVariantId(null);
                setEditGuidance('');
              }}
            >
              {t.common.cancel}
            </button>
          </div>
        </form>
      ) : null}

      {zoomedIndex !== null ? (
        <ImageViewer
          items={variants.map((variant, index) => ({
            src: variant.url,
            alt: `${labels.variantsTitle} ${index + 1}`,
          }))}
          initialIndex={zoomedIndex}
          onClose={() => setZoomedIndex(null)}
        />
      ) : null}
    </div>
  );
}
